import './kebab-menu.css';

/**
 * Reusable "three dots" (kebab) overflow menu for secondary page actions.
 *
 * Usage:
 *   const menu = createKebabMenu({
 *     label: 'Inventory actions',
 *     items: [
 *       { label: 'Import Lite n Easy menu', icon: 'fa-file-import', onSelect: () => {...} },
 *     ],
 *   });
 *   toolbar.appendChild(menu);
 *
 * Each item: { label, icon? (Font Awesome class minus "fa-solid"), onSelect, danger? }.
 *
 * The whole thing is data-driven so other pages can drop in their own action
 * lists. The returned element is a <div> wrapper containing the trigger button
 * and the (hidden until opened) dropdown.
 */
export function createKebabMenu({ label = 'More actions', items = [] } = {}) {
  const wrap = document.createElement('div');
  wrap.className = 'kebab';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'icon-button kebab-trigger';
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', label);
  trigger.title = label;
  trigger.innerHTML = '<i class="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>';

  const menu = document.createElement('div');
  menu.className = 'kebab-menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  for (const item of items) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kebab-item';
    if (item.danger) btn.classList.add('kebab-item-danger');
    btn.setAttribute('role', 'menuitem');
    // Build with DOM APIs (never innerHTML): labels often embed user data
    // (e.g. `Delete ${name}`), so they must be inserted as text.
    const icon = document.createElement(item.icon ? 'i' : 'span');
    icon.className = item.icon ? `fa-solid ${item.icon}` : 'kebab-item-icon-placeholder';
    icon.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.textContent = item.label ?? '';
    btn.append(icon, text);
    btn.addEventListener('click', () => {
      close();
      item.onSelect?.();
    });
    menu.appendChild(btn);
  }

  function open() {
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', onOutsideClick, true);
    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onOutsideClick, true);
    document.removeEventListener('keydown', onKeydown, true);
  }

  function toggle() {
    if (menu.hidden) open();
    else close();
  }

  function onOutsideClick(event) {
    if (!wrap.contains(event.target)) close();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      trigger.focus();
    }
  }

  trigger.addEventListener('click', (event) => {
    event.stopPropagation();
    toggle();
  });

  wrap.append(trigger, menu);
  return wrap;
}
